import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import Appbar from 'components/appbar/Appbar';
import ProcedureForm from '../form/ProcedureForm';
import { useProcedureValidation } from '../validation/useProcedureValidation';
import ProcedureTab from '../tab/ProcedureTab';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { useParams } from 'react-router-dom';
import InsideLoading from 'components/loading/InsideLoading';
import NoData from 'components/no-data/NoData';
import { moneyFormat } from 'helpers/moneyFormat';
import { Procedure, ProcedureDoctor } from 'types/procedure';
import ProcedureDoctors from '../ProcedureDoctors';
import ProcedureSpecialtiesForm from '../form/ProcedureSpecialtiesForm';
import { Specialty } from 'types/specialty';
import {
  ContentPad,
  PageShell,
  RecordActions,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

// Só o que o "Salvar" grava: serve para saber se há alterações pendentes.
function snapshot(procedure: Procedure | null) {
  if (!procedure) return '';

  return JSON.stringify({
    name: procedure.name,
    sale_price: String(procedure.sale_price),
    commission: String(procedure.commission),
    cost: String(procedure.cost),
    sum_commission_to_total: Boolean(procedure.sum_commission_to_total),
    active: Number(procedure.active),
    specialties: (procedure.specialties || []).map(item => [item.specialty_id, Number(item.active)]).sort(),
  });
}

const EditProcedure: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation, validate] = useProcedureValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  // como a consulta está gravada: a gravação de médicos parte daqui, sem levar junto o que ainda não foi salvo
  const [saved, setSaved] = useState<Procedure | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');

  const dirty = useMemo(() => snapshot(procedure) !== savedSnapshot, [procedure, savedSnapshot]);

  // nome e preço ficam na aba Geral: abre ela quando a validação reclama de um deles
  useEffect(() => {
    if (Object.keys(validation).length) setTab('main');
  }, [validation]);

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => {
        setSpecialties(response.data);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar as especialidades');
      })
      .finally(() => setLoadingSpecialties(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);

    api
      .get(`/procedures/${id}`)
      .then(response => {
        const procedureData = response.data;
        const loaded: Procedure = {
          ...procedureData,
          type: procedureData.type || 'G',
          // a API exige número; cadastros antigos podem vir sem esses valores
          sale_price: procedureData.sale_price ?? 0,
          cost: procedureData.cost ?? 0,
          commission: procedureData.commission ?? 0,
          specialties: procedureData.specialties.map(item => ({ ...item, active: 1 })) || [],
        };

        setProcedure(loaded);
        setSaved(loaded);
        setSavedSnapshot(snapshot(loaded));
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados da consulta');
        history.push('/consultation');
      })
      .finally(() => setLoading(false));
  }, [id]);

  function handleChangeSpecialties(selectedIds: number[]) {
    if (!procedure) return;

    // Lista atual das specialties do procedimento
    const current = procedure.specialties || [];

    // 1) SPECIALTIES SELECIONADAS
    const addedOrUpdated = selectedIds.map(sid => {
      const existing = current.find(ps => ps.specialty_id === sid);

      return existing
        ? { ...existing, active: 1 } // já existia → só garante active = 1
        : {
            id: 0, // novo relacionamento
            specialty_id: sid,
            active: 1,
            specialty: specialties.find(s => s.id === sid) || null,
          };
    });

    // 2) SPECIALTIES REMOVIDAS
    const removed = current.filter(ps => !selectedIds.includes(ps.specialty_id)).map(ps => ({ ...ps, active: 0 }));

    // 3) Resultado final
    const updated = [...addedOrUpdated, ...removed];

    setProcedure(prev => (prev ? { ...prev, specialties: updated } : null));
  }

  function handleChange<K extends keyof Procedure>(index: K, value: Procedure[K]) {
    // Se o index for specialties, o value é um array de números (ids das especialidades)
    // Mas o procedure.specialties é um array de ProcedureSpecialty
    // Então, precisamos mapear os ids para ProcedureSpecialty
    // Caso alguma specialty tenha sido removida, devemos setar ela com active: 0
    // Caso alguma specialty tenha sido adicionada, devemos setar ela com active: 1 e id: 0
    // Caso alguma specialty não tenha sido alterada, mantemos como está
    // E adicionar os novos com active: 1 e id: 0
    // Se existir id no specialty, manter o id
    // Se não existir id, sete como 0 e active como 1
    // Caso não exista id e estiver sendo removido, pode remover normalmente
    // Lembre-se de setar o specialty_id corretamente
    // Ou seja, o specialty_id é o id da specialty, e o id é o id do relacionamento
    // Então, se a specialty já existir no procedure.specialties, mantenha o id
    setProcedure(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!procedure) return;

    setSaving(true);

    api
      .put(`/procedures/${procedure.id}`, {
        ...procedure,
      })
      .then(() => {
        toast.success('Consulta atualizada com sucesso!');
        history.push('/consultation');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar a consulta');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!procedure) return;

    validate(procedure)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // Médicos são gravados na hora, a partir do que já está salvo.
  function saveDoctor(doctor: ProcedureDoctor & { active?: number }, errorMessage: string) {
    if (!saved) return;

    api
      .put(`/procedures/${saved.id}`, { ...saved, doctors: [doctor] })
      .then(response => {
        toast.success('Alteração salva');
        setProcedure(state => (state ? { ...state, doctors: response.data.doctors } : null));
        setSaved(state => (state ? { ...state, doctors: response.data.doctors } : null));
      })
      .catch(err => {
        toast.error(err.response?.data?.error || errorMessage);
      });
  }

  const handleChangeAddDoctor = (doctor: ProcedureDoctor) =>
    saveDoctor(doctor, 'Não foi possível adicionar o médico à consulta');
  const handleDeleteDoctor = (doctor: ProcedureDoctor) =>
    saveDoctor({ ...doctor, active: 0 }, 'Não foi possível excluir o médico da consulta');

  const counts = {
    doctors: procedure?.doctors?.length || 0,
    specialties: procedure?.specialties?.filter(item => item.active !== 0).length || 0,
  };

  function goBack() {
    history.push('/consultation');
  }

  return (
    <>
      <Appbar title="Consultas" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title={saved?.name || 'Consulta'} active={!!saved?.active} backAction={goBack}>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`Preço ${moneyFormat(saved?.sale_price || 0)}`}
          />
        </RecordHeader>

        <WorkArea>
          <ProcedureTab onChange={newTab => setTab(newTab)} tab={tab} counts={counts} />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !procedure ? (
              <NoData message="Não foi possível carregar os dados da consulta" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'doctors' ? (
                  <ProcedureDoctors
                    procedure={procedure}
                    handleDeleteDoctor={handleDeleteDoctor}
                    handleChangeAddDoctor={handleChangeAddDoctor}
                  />
                ) : tab === 'specialties' ? (
                  <ProcedureSpecialtiesForm
                    handleChangeSpecialties={handleChangeSpecialties}
                    procedure={procedure}
                    loadingSpecialties={loadingSpecialties}
                    specialties={specialties}
                  />
                ) : (
                  <ProcedureForm handleChange={handleChange} procedure={procedure} validation={validation} />
                )}
                {tab === 'doctors' && <RecordNote>Os médicos são salvos na hora, ao adicionar ou remover.</RecordNote>}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {tab !== 'doctors' && !loading && procedure && (
          <RecordActions
            saving={saving}
            saveLabel="Salvar alterações"
            dirty={dirty}
            onSave={() => handleValidation()}
          />
        )}
      </PageShell>
    </>
  );
};

export default EditProcedure;
